import { getFileIconHtml } from "./documentHelper";

export const applyTravelTemplate = (html, pages = [], userName = "", bannerImage = "", footerImage = "") => {
  let processedHtml = html || "";
  if (userName) {
    processedHtml = processedHtml.replace(/{%user_name%}/g, userName);
  }
  const fallbackImage = "https://open-domo-portals.s3.us-west-2.amazonaws.com/form_file-2f036911hmquzauxe-0";
  const resolvedBanner = bannerImage || fallbackImage;
  processedHtml = processedHtml.replace(/{%banner_image%}/g, resolvedBanner);
  const resolvedFooter = footerImage || null;
  processedHtml = resolvedFooter ? processedHtml.replace(/{%footer_image%}/g, resolvedFooter) : processedHtml;

  const parser = new DOMParser();
  const doc = parser.parseFromString(processedHtml, "text/html");

  const list = Array.isArray(pages) ? pages : [];

  const resolveImage = (img) => {
    if (!img) return "";
    const trimmed = img.trim();
    // {%page_image%} is substituted directly into a `src="{%page_image%}"`
    // attribute, so this must always resolve to a plain URL-safe string —
    // never raw markup, or the quotes inside it break the attribute.
    if (trimmed.startsWith("<svg")) return `data:image/svg+xml,${encodeURIComponent(trimmed)}`;
    return trimmed;
  };

  const setPageAttributes = (node, page) => {
    if (!node || !page) return;
    if (page?.slug) node.setAttribute("data-page-slug", page.slug);
    if (page?._id) node.setAttribute("data-page-id-val", page._id);
    if (page?.groupId || page?.group_id) node.setAttribute("data-group-id", page.groupId || page.group_id);
    if (page?.link) node.setAttribute("data-item-link", page.link);
    if (page?.page_type) node.setAttribute("data-page-type", page.page_type);
    if (page?.page_link) node.setAttribute("data-page-link", page.page_link);
    if (page?.page_link_newtab != null) node.setAttribute("data-page-link-newtab", page.page_link_newtab ? "true" : "false");
    if (page?.form_id || page?.content_type === "form") node.setAttribute("data-content-type", "form");
    if (page?.document_id || page?.content_type === "document") node.setAttribute("data-content-type", "document");
  };

  const injectVars = (str, page) =>
    (str || "")
      .replace(/{%page_name%}/g, page?.name || "Unnamed Page")
      .replace(/{%page_image%}/g, resolveImage(page?.image))
      .replace(/{%page_type%}/g, page?.page_type || "")
      .replace(/{%page_link%}/g, page?.page_link || "")
      .replace(/{%page_link_newtab%}/g, page?.page_link_newtab ? "true" : "false")
      .replace(/{%page_slug%}/g, page?.slug || "")
      .replace(/{%user_name%}/g, userName)
      .replace(/{%banner_image%}/g, resolvedBanner)
      .replace(/{%footer_image%}/g, resolvedFooter);

  let proto = doc.getElementById("page_item");
  let container = proto ? proto.parentElement : null;
  let isToolsGridProto = false;

  if (!proto) {
    const toolsGrid = doc.getElementById("tools_grid") || doc.querySelector(".tools-grid");
    if (toolsGrid) {
      container = toolsGrid;
      if (toolsGrid.firstElementChild) {
        proto = toolsGrid.firstElementChild;
        isToolsGridProto = true;
      } else {
        const defaultProto = doc.createElement("div");
        defaultProto.className = "tool-tile";
        defaultProto.innerHTML = `
          <img src="{%page_image%}" class="tile-icon" alt="{%page_name%}" />
          <span>{%page_name%}</span>
        `;
        proto = defaultProto;
        isToolsGridProto = true;
      }
    }
  }

  let templateNode = null;
  if (proto) {
    templateNode = proto.cloneNode(true);
    templateNode.removeAttribute("id");
    templateNode.removeAttribute("data-page-id");
  }

  if (container) {
    if (proto && proto.id === "page_item" && proto.parentElement === container) {
      proto.remove();
    } else if (isToolsGridProto) {
      Array.from(container.children).forEach((child) => {
        if (!child.hasAttribute("data-page-id")) {
          child.remove();
        }
      });
    }
  }

  list.forEach((page) => {
    let matchedInDOM = false;

    const idNode = page?._id ? doc.querySelector(`[data-page-id="${page._id}"]`) : null;
    if (idNode) {
      const updatedHtml = injectVars(idNode.outerHTML, page);
      const temp = doc.createElement("div");
      temp.innerHTML = updatedHtml;
      const newNode = temp.firstElementChild;
      if (newNode) {
        setPageAttributes(newNode, page);
        newNode.removeAttribute("data-page-id");
        idNode.replaceWith(newNode);
      }
      matchedInDOM = true;
    }

    const node = page?.slug ? doc.getElementById(page.slug) : null;
    if (node && !matchedInDOM) {
      const updatedHtml = injectVars(node.outerHTML, page);
      const temp = doc.createElement("div");
      temp.innerHTML = updatedHtml;
      const newNode = temp.firstElementChild;
      if (newNode) {
        setPageAttributes(newNode, page);
        node.replaceWith(newNode);
      }
      matchedInDOM = true;
    }

    if (templateNode && container && !matchedInDOM) {
      const clonedHtml = injectVars(templateNode.outerHTML, page);
      const temp = doc.createElement("div");
      temp.innerHTML = clonedHtml;
      const newNode = temp.firstElementChild;
      if (newNode) {
        setPageAttributes(newNode, page);
        container.appendChild(newNode);
      }
    }
  });

  // Remove any elements that still have data-page-id (since active ones had it removed)
  doc.querySelectorAll("[data-page-id]").forEach((node) => {
    node.remove();
  });

  // Swap the placeholder <img src="data:image/svg+xml,..."> back into a real
  // inline <svg>, so icons inherit currentColor/text color from the tile
  // instead of being locked to whatever color was baked into the markup.
  doc.querySelectorAll('img[src^="data:image/svg+xml,"]').forEach((imgEl) => {
    const encoded = imgEl.getAttribute("src").slice("data:image/svg+xml,".length);
    let svgMarkup;
    try {
      svgMarkup = decodeURIComponent(encoded);
    } catch {
      return;
    }
    const svgEl = parser.parseFromString(svgMarkup, "image/svg+xml").documentElement;
    if (!svgEl || svgEl.nodeName.toLowerCase() !== "svg") return;
    if (imgEl.className) svgEl.setAttribute("class", imgEl.className);
    const style = imgEl.getAttribute("style") || "";
    svgEl.setAttribute("style", `${style}${style && !style.trim().endsWith(";") ? ";" : ""}color:#fff;`);
    imgEl.replaceWith(doc.importNode(svgEl, true));
  });

  return "<!doctype html>\n" + doc.documentElement.outerHTML;
};

export const applyListTemplate = (html, pageName = "Documents", docs = []) => {
  const tplWithPage = (html || "").replace(/{%page_name%}/g, pageName);

  const parser = new DOMParser();
  const doc = parser.parseFromString(tplWithPage, "text/html");

  const list = Array.isArray(docs) ? docs : [];

  list.forEach((d) => {
    let node = doc.getElementById(d.slug);
    const docName = d?.name || "Unnamed Document";
    const ext = d?.extension || d?.file?.extension || "";
    const docIcon = ext
      ? getFileIconHtml(ext)
      : `
      <div style="width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); border: 1px solid #fde68a; background-color: #fffbeb; color: #d97706;">
<svg viewBox="0 0 1024 1024" class="icon" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M853.333333 256H469.333333l-85.333333-85.333333H170.666667c-46.933333 0-85.333333 38.4-85.333334 85.333333v170.666667h853.333334v-85.333334c0-46.933333-38.4-85.333333-85.333334-85.333333z" fill="#FFA000"></path><path d="M853.333333 256H170.666667c-46.933333 0-85.333333 38.4-85.333334 85.333333v426.666667c0 46.933333 38.4 85.333333 85.333334 85.333333h682.666666c46.933333 0 85.333333-38.4 85.333334-85.333333V341.333333c0-46.933333-38.4-85.333333-85.333334-85.333333z" fill="#FFCA28"></path></g></svg>
      </div>
    `;
    if (node) {
      // Found by slug → replace contents in place
      let currentHTML = node.innerHTML || "";
      if (!currentHTML.includes("{%document_icon%}")) {
        currentHTML = currentHTML.replace(/{%document_name%}/g, `<div style="display: flex; align-items: center; gap: 8px; width: 100%;">{%document_icon%} <span>{%document_name%}</span></div>`);
      }
      node.innerHTML = currentHTML.replace(/{%document_name%}/g, docName).replace(/{%document_icon%}/g, docIcon) || docName;
      if (d?._id) node.setAttribute("data-doc-id", d._id);
    }

    // Fall back to the "document_item" prototype (all documents in the loop)
    const proto = doc.getElementById("document_item");
    if (proto && proto.parentElement) {
      const clone = proto.cloneNode(true);
      clone.removeAttribute("id");
      let currentHTML = clone.innerHTML || "";
      if (!currentHTML.includes("{%document_icon%}")) {
        currentHTML = currentHTML.replace(/{%document_name%}/g, `<div style="display: flex; align-items: center; gap: 8px; width: 100%;">{%document_icon%} <span>{%document_name%}</span></div>`);
      }
      clone.innerHTML = currentHTML.replace(/{%document_name%}/g, docName).replace(/{%document_icon%}/g, docIcon) || docName;
      if (d?._id) clone.setAttribute("data-doc-id", d._id);
      proto.parentElement.appendChild(clone);
    }
  });

  const proto = doc.getElementById("document_item");
  if (proto && proto.parentElement) proto.remove();

  return "<!doctype html>\n" + doc.documentElement.outerHTML;
};

export const applyMenuTemplate = (html, menus = [], currentUrl = "") => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html || "", "text/html");

  const menuProto = doc.getElementById("menu_item");
  const sidebar = doc.getElementById("sidebar");

  if (!menuProto || !sidebar) return html;

  const templateCss = Array.from(doc.querySelectorAll("style"))
    .map((s) => s.textContent || "")
    .join("\n");
  const cssHas = (re) => re.test(templateCss);

  const htmlControlsActive = cssHas(/\.active\b/);
  const htmlControlsIcon = cssHas(/\.menu-icon\b/);
  const htmlControlsSubmenu = cssHas(/\.submenu-container\b/);
  const htmlControlsScrollbar = cssHas(/::-webkit-scrollbar/);
  const htmlControlsRotate = cssHas(/\.chevron\.rotate|\.rotate\b/);
  const htmlHasChevron = !!menuProto.querySelector(".chevron");

  const normalize = (url) => (typeof url === "string" ? url.replace(/\/$/, "") : "");
  const current = normalize(currentUrl);

  const cloneProto = () => {
    const node = menuProto.cloneNode(true);
    node.removeAttribute("id");
    return node;
  };

  const injectIcon = (node, image) => {
    if (!image) return;
    const iconSpan = doc.createElement("span");
    const isInlineSvg = image.trim().startsWith("<");

    if (htmlControlsIcon) {
      iconSpan.className = "menu-icon";
      iconSpan.innerHTML = isInlineSvg ? image : `<img src="${image}" alt="menu icon" />`;
    } else {
      iconSpan.innerHTML = isInlineSvg ? image : `<img src="${image}" alt="menu icon" class="menu-icon w-5 h-5 object-contain" />`;
      iconSpan.classList.add("menu-icon", "w-5", "h-5", "object-contain");
    }

    node.insertBefore(iconSpan, node.querySelector(".menu-label, .flex-1"));
  };
  const createChevron = () => {
    const chevron = doc.createElement("span");
    chevron.className = "chevron";
    chevron.setAttribute("data-toggle", "1");
    chevron.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
      </svg>
    `;
    return chevron;
  };

  const isActiveItem = (item) => item?.isActive === true || (typeof item?.link === "string" && item.link.trim() && current === normalize(item.link));

  const hasActiveMenu = menus.some((menu) => {
    if (isActiveItem(menu)) return true;
    return Array.isArray(menu?.submenu) && menu.submenu.some((sub) => isActiveItem(sub));
  });

  const shouldUseDefaultActive = !hasActiveMenu && menus.length > 0;

  const createSingleMenuItem = (item, isDefaultActive = false) => {
    const node = cloneProto();
    node.querySelector(".chevron")?.remove();
    injectIcon(node, item.image);
    node.innerHTML = node.innerHTML.replace(/{%menu_name%}/g, item.name);
    node.setAttribute("data-link", item.link || "#");
    node.setAttribute("data-type", "single");

    const isActive = isActiveItem(item) || isDefaultActive;

    if (isActive) {
      node.classList.add("active");
    }

    return { node, hasActive: isActive };
  };

  const createGroupMenuItem = (item, depth = 0, isDefaultActive = false) => {
    const node = cloneProto();
    injectIcon(node, item.image);
    node.innerHTML = node.innerHTML.replace(/{%menu_name%}/g, item.name);
    node.setAttribute("data-link", "#");
    node.setAttribute("data-type", "group");
    node.classList.add("has_submenu");

    if (!node.querySelector(".chevron")) node.appendChild(createChevron());

    const groupId = "group-" + Math.random().toString(36).slice(2, 8);
    node.setAttribute("data-group-id", groupId);

    const submenuContainer = doc.createElement("div");
    submenuContainer.className = "submenu-container";
    submenuContainer.setAttribute("data-group-id", groupId);

    let hasActiveChild = false;

    item.submenu.forEach((sub) => {
      const result = createMenuItem(sub, depth + 1, isDefaultActive && !hasActiveChild);
      submenuContainer.appendChild(result.node);
      if (result.hasActive) hasActiveChild = true;
    });

    if (isActiveItem(item) || (isDefaultActive && !hasActiveChild)) {
      node.classList.add("active");
    }

    if (hasActiveChild || item?.isOpen === true) {
      submenuContainer.classList.add("open");
      node.querySelector(".chevron")?.classList.add("rotate");
    }

    const wrapper = doc.createElement("div");
    wrapper.className = htmlControlsSubmenu ? "" : "mb-1";
    wrapper.appendChild(node);
    wrapper.appendChild(submenuContainer);

    return { node: wrapper, hasActive: hasActiveChild };
  };

  const createMenuItem = (item, depth = 0, isDefaultActive = false) => {
    const isGroup = Array.isArray(item.submenu) && item.submenu.length > 0;
    return isGroup ? createGroupMenuItem(item, depth, isDefaultActive) : createSingleMenuItem(item, isDefaultActive);
  };

  menus.forEach((menu, index) => {
    const result = createMenuItem(menu, 0, shouldUseDefaultActive && index === 0);
    sidebar.appendChild(result.node);
  });

  menuProto.remove();

  const fallbackCss = [];

  if (!htmlControlsSubmenu) {
    fallbackCss.push(`
      .submenu-container {
        margin-top: 4px;
        margin-left: 16px;
        display: none;
      }
      .submenu-container.open {
        display: block;
      }`);
  }

  if (!htmlControlsActive) {
    fallbackCss.push(`
      .active {
        background: white !important;
        color: #0f766e !important;
      }`);
  }

  if (!htmlControlsScrollbar) {
    fallbackCss.push(`
      #sidebar::-webkit-scrollbar {
        width: 6px;
      }
      #sidebar::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.3);
        border-radius: 10px;
      }`);
  }

  if (!htmlControlsRotate) {
    fallbackCss.push(`
      .chevron.rotate svg {
        transform: rotate(${htmlHasChevron ? 90 : 180}deg);
      }`);
  }

  const style = fallbackCss.length ? `<style>${fallbackCss.join("\n")}</style>` : "";

  const script = `
  <script>
    document.addEventListener("click", (e) => {
      const menuItem = e.target.closest(".menu-header");
      if (!menuItem) return;

      const type = menuItem.getAttribute("data-type");

      if (type === "group") {
        const groupId = menuItem.getAttribute("data-group-id");
        const container = document.querySelector('.submenu-container[data-group-id="' + groupId + '"]');
        if (container) {
          container.classList.toggle("open");
          menuItem.querySelector(".chevron")?.classList.toggle("rotate");
        }
        e.stopPropagation();
        return;
      }

      if (type === "single") {
        const link = menuItem.dataset.link;
        if (link && link !== "#") {
          document.querySelectorAll(".active").forEach(el => el.classList.remove("active"));
          menuItem.classList.add("active");
          window.parent.postMessage({ type: "NAVIGATE", link }, "*");
        }
        e.stopPropagation();
      }
    });
  </script>
`;

  return "<!doctype html>\n" + doc.documentElement.outerHTML.replace("</body>", style + script + "</body>");
};

// export const applyMenuTemplate = (html, menus = [], currentUrl = "") => {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(html || "", "text/html");

//   const menuProto = doc.getElementById("menu_item");
//   const sidebar = doc.getElementById("sidebar");

//   if (!menuProto || !sidebar) return html;

//   const normalize = (url) => (typeof url === "string" ? url.replace(/\/$/, "") : "");
//   const current = normalize(currentUrl);

//   const cloneProto = () => {
//     const node = menuProto.cloneNode(true);
//     node.removeAttribute("id");
//     return node;
//   };

//   const injectIcon = (node, image) => {
//     if (!image) return;
//     const iconSpan = doc.createElement("span");
//     iconSpan.innerHTML = image.includes("svg") ? image : `<img src="${image}" alt="menu icon" class="menu-icon w-5 h-5 object-contain" />`;
//     iconSpan.classList.add("menu-icon", "w-5", "h-5", "object-contain");
//     node.insertBefore(iconSpan, node.querySelector(".flex-1"));
//   };

//   const createChevron = () => {
//     const chevron = doc.createElement("span");
//     chevron.className = "chevron";
//     chevron.setAttribute("data-toggle", "1");
//     chevron.innerHTML = `
//       <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
//       </svg>
//     `;
//     return chevron;
//   };

//   const createSingleMenuItem = (item) => {
//     const node = cloneProto();
//     injectIcon(node, item.image);
//     node.innerHTML = node.innerHTML.replace(/{%menu_name%}/g, item.name);
//     node.setAttribute("data-link", item.link || "#");
//     node.setAttribute("data-type", "single");

//     if (current === normalize(item.link)) {
//       node.classList.add("active");
//     }

//     return { node, hasActive: current === normalize(item.link) };
//   };

//   const createGroupMenuItem = (item, depth = 0) => {
//     const node = cloneProto();
//     injectIcon(node, item.image);
//     node.innerHTML = node.innerHTML.replace(/{%menu_name%}/g, item.name);
//     node.setAttribute("data-link", "#");
//     node.setAttribute("data-type", "group");
//     node.classList.add("has_submenu");
//     node.appendChild(createChevron());

//     const groupId = "group-" + Math.random().toString(36).slice(2, 8);
//     node.setAttribute("data-group-id", groupId);

//     const submenuContainer = doc.createElement("div");
//     submenuContainer.className = "submenu-container";
//     submenuContainer.setAttribute("data-group-id", groupId);

//     let hasActiveChild = false;

//     item.submenu.forEach((sub) => {
//       const result = createMenuItem(sub, depth + 1);
//       submenuContainer.appendChild(result.node);
//       if (result.hasActive) hasActiveChild = true;
//     });

//     if (hasActiveChild) {
//       submenuContainer.classList.add("open");
//       node.querySelector(".chevron")?.classList.add("rotate");
//     }

//     const wrapper = doc.createElement("div");
//     wrapper.className = "mb-1";
//     wrapper.appendChild(node);
//     wrapper.appendChild(submenuContainer);

//     return { node: wrapper, hasActive: hasActiveChild };
//   };

//   const createMenuItem = (item, depth = 0) => {
//     const isGroup = Array.isArray(item.submenu) && item.submenu.length > 0;
//     return isGroup ? createGroupMenuItem(item, depth) : createSingleMenuItem(item);
//   };

//   menus.forEach((menu) => {
//     const result = createMenuItem(menu);
//     sidebar.appendChild(result.node);
//   });

//   menuProto.remove();

//   const style = `
//     <style>
//       .submenu-container {
//         margin-top: 4px;
//         margin-left: 16px;
//         display: none;
//       }
//       .submenu-container.open {
//         display: block;
//       }
//       .active {
//         background: white !important;
//         color: #0f766e !important;
//       }
//       #sidebar::-webkit-scrollbar {
//         width: 6px;
//       }
//       #sidebar::-webkit-scrollbar-thumb {
//         background: rgba(255,255,255,0.3);
//         border-radius: 10px;
//       }
//       .chevron.rotate svg {
//         transform: rotate(180deg);
//       }
//     </style>
//   `;

//   const script = `
//   <script>
//     document.addEventListener("click", (e) => {
//       const menuItem = e.target.closest(".menu-header");
//       if (!menuItem) return;

//       const type = menuItem.getAttribute("data-type");

//       if (type === "group") {
//         const groupId = menuItem.getAttribute("data-group-id");
//         const container = document.querySelector('.submenu-container[data-group-id="' + groupId + '"]');
//         if (container) {
//           container.classList.toggle("open");
//           menuItem.querySelector(".chevron")?.classList.toggle("rotate");
//         }
//         e.stopPropagation();
//         return;
//       }

//       if (type === "single") {
//         const link = menuItem.dataset.link;
//         if (link && link !== "#") {
//           document.querySelectorAll(".active").forEach(el => el.classList.remove("active"));
//           menuItem.classList.add("active");
//           window.parent.postMessage({ type: "NAVIGATE", link }, "*");
//         }
//         e.stopPropagation();
//       }
//     });
//   </script>
// `;

//   return "<!doctype html>\n" + doc.documentElement.outerHTML.replace("</body>", style + script + "</body>");
// };
